import { useState, useRef, useEffect } from 'react'
import { Plus, Paperclip, X } from 'lucide-react'
import { addTimelineEntry } from '../../api/timeline'
import { uploadAttachment, isAllowedMime } from '../../api/attachments'
import { Button } from '../ui/Button'
import { NoteTimeInput, StatusLabelInput } from './NoteFields'
import { fromLocalInputValue } from './noteTime'

interface AddTimelineEntryProps {
  incidentId: string
  onSuccess: (message: string) => void
  onError: (message: string) => void
  onEntryAdded: () => void
}

export function AddTimelineEntry({
  incidentId,
  onSuccess,
  onError,
  onEntryAdded,
}: AddTimelineEntryProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [message, setMessage] = useState('')
  const [isPublicUpdate, setIsPublicUpdate] = useState(false)
  const [occurredAt, setOccurredAt] = useState('') // '' = now
  const [statusLabel, setStatusLabel] = useState('Investigating')
  const [files, setFiles] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Clipboard paste — attach images when the form is open
  useEffect(() => {
    if (!isExpanded) return
    function handlePaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of Array.from(items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (!file) continue
          const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
          const named = new File([file], `screenshot-${ts}.png`, { type: file.type })
          setFiles((prev) => [...prev, named])
        }
      }
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [isExpanded])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if ((!message.trim() && files.length === 0) || isSubmitting) return

    setIsSubmitting(true)

    try {
      // Upload files first — if this fails, the note is never posted
      for (const file of files) {
        await uploadAttachment(incidentId, file)
      }

      if (message.trim()) {
        await addTimelineEntry(incidentId, {
          type: isPublicUpdate ? 'public_update' : 'message',
          content: {
            message: message.trim(),
            ...(isPublicUpdate && statusLabel.trim() ? { status_label: statusLabel.trim() } : {}),
          },
          ...(occurredAt ? { timestamp: fromLocalInputValue(occurredAt) } : {}),
        })
      }

      const successMsg = isPublicUpdate && message.trim()
        ? 'Public update posted'
        : files.length > 0 && message.trim()
          ? 'Note and attachment added'
          : files.length > 0
            ? `${files.length} file${files.length > 1 ? 's' : ''} attached`
            : 'Note added to timeline'

      onSuccess(successMsg)
      setMessage('')
      setIsPublicUpdate(false)
      setOccurredAt('')
      setStatusLabel('Investigating')
      setFiles([])
      setIsExpanded(false)
      onEntryAdded()
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to add note')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setMessage('')
    setIsPublicUpdate(false)
    setOccurredAt('')
    setStatusLabel('Investigating')
    setFiles([])
    setIsExpanded(false)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const valid = Array.from(e.target.files).filter((f) => {
      if (!isAllowedMime(f)) {
        onError(`"${f.name}" is not a supported file type`)
        return false
      }
      if (f.size > 10 * 1024 * 1024) {
        onError(`"${f.name}" exceeds the 10 MB limit`)
        return false
      }
      return true
    })
    setFiles((prev) => [...prev, ...valid])
    e.target.value = ''
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add a note
      </button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="border border-border rounded-lg p-4">
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Add a note to the timeline..."
        rows={3}
        autoFocus
        disabled={isSubmitting}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none disabled:opacity-50"
      />

      {/* Attached file chips */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {files.map((f, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-surface-secondary border border-border rounded-full text-text-secondary"
            >
              <Paperclip className="w-3 h-3" />
              {f.name}
              {!isSubmitting && (
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="ml-0.5 hover:text-red-500 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <label className="flex items-center gap-2 mt-3 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={isPublicUpdate}
          onChange={(e) => setIsPublicUpdate(e.target.checked)}
          disabled={isSubmitting}
          className="rounded border-border"
        />
        Post as public update to the Slack feed
      </label>
      <p className="mt-1 text-xs text-text-tertiary">
        Public updates also appear on the status page when this incident is public.
      </p>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
        <NoteTimeInput value={occurredAt} onChange={setOccurredAt} disabled={isSubmitting} placeholderNow />
        {isPublicUpdate && <StatusLabelInput value={statusLabel} onChange={setStatusLabel} disabled={isSubmitting} />}
      </div>

      <div className="flex items-center justify-between mt-3">
        {/* Attach file button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSubmitting}
          className="flex items-center gap-1.5 px-2 py-1 text-xs text-text-tertiary hover:text-text-primary hover:bg-surface-secondary rounded transition-colors disabled:opacity-50"
          title="Attach file"
        >
          <Paperclip className="w-3.5 h-3.5" />
          Attach
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*,application/pdf,text/plain,text/csv,application/json,application/zip"
          onChange={handleFileChange}
        />

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={(!message.trim() && files.length === 0) || isSubmitting}
            loading={isSubmitting}
          >
            {isPublicUpdate ? 'Post update' : 'Add note'}
          </Button>
        </div>
      </div>
    </form>
  )
}
